import ArrowDownLeft from "~icons/ph/arrow-down-left";
import ArrowUpRight from "~icons/ph/arrow-up-right";
import Check from "~icons/ph/check";
import MessageCircle from "~icons/ph/chat-circle";
import Phone from "~icons/ph/phone";
import PhoneOff from "~icons/ph/phone-slash";
import UserPlus from "~icons/ph/user-plus";
import Video from "~icons/ph/video-camera";
import X from "~icons/ph/x";
import type { CallHistory, Person } from "../../shared/types";
import type { ConnectionAction } from "../../shared/api";
import { useShell } from "../app/shell";
import { ApiError } from "../data/api";
import {
  useConnection,
  useRemoveRecent,
  useSubmitting,
} from "../data/mutations";
import { useAccount } from "../data/session";
import { dateTime, day, duration, t, time } from "../i18n";
import { isMissedCall } from "../notifications/missed-calls";
import { ActivityStatus, PersonMenu, ProfileAvatar } from "../ui/components";
import { notify } from "../ui/toast";

export function useConnectionAction() {
  const connection = useConnection(),
    submitting = useSubmitting();
  return async function requestAction(
    p: Pick<Person, "id" | "name">,
    action: ConnectionAction,
    code?: string,
  ) {
    if (submitting) return;
    try {
      await connection.mutateAsync({
        action,
        peer: p.id,
        code: action === "request" ? code : undefined,
      });
      notify(
        action === "request"
          ? t("تم إرسال طلب الإضافة")
          : action === "accept"
            ? t("تمت إضافة {name} إلى الأصدقاء", { name: p.name })
            : action === "decline"
              ? t("تم رفض الطلب")
              : action === "block"
                ? t("تم حظر الحساب")
                : action === "unblock"
                  ? t("تم إلغاء الحظر")
                  : action === "remove-friend"
                    ? t("تمت إزالة الصديق")
                    : t("تم إلغاء الطلب"),
        "success",
      );
      return true;
    } catch (e) {
      if (action === "request" && e instanceof ApiError && e.retryAfter)
        notify(
          t("يمكنك إرسال طلب آخر إلى {name} بعد {date}.", {
            name: p.name,
            date: dateTime(Date.now() + e.retryAfter * 1000),
          }),
        );
      else notify((e as Error).message);
      return false;
    }
  };
}

export function SafetyMenu({
  person,
  isFriend,
  onRemoveRecent,
}: {
  person: Person;
  isFriend: boolean;
  onRemoveRecent?: () => void;
}) {
  const { chooseSafety } = useShell();
  const account = useAccount(),
    submitting = useSubmitting();
  if (!account) return null;
  return (
    <PersonMenu
      person={person}
      isFriend={isFriend}
      disabled={submitting}
      onChoose={chooseSafety}
      onRemoveRecent={onRemoveRecent}
    />
  );
}

export function PersonRow({
  person: p,
  kind = "search",
  onOpen: openProfile,
}: {
  person: Person;
  kind?: "search" | "friend" | "incoming" | "outgoing";
  onOpen?: (person: Person) => void;
}) {
  const shell = useShell();
  const account = useAccount(),
    submitting = useSubmitting();
  const requestAction = useConnectionAction();
  const onOpen = openProfile ?? shell.openPerson;
  const isFriend = account?.friends.some((f) => f.id === p.id);
  const hasLastActive = p.status === "offline" && p.last_active_at != null;
  return (
    <div className="person-row">
      <ProfileAvatar person={p} dot={isFriend} onOpen={onOpen} />
      <button
        className="person-name"
        aria-label={t("عرض حساب {name}", { name: p.name })}
        aria-description={
          isFriend && p.status === "offline" && p.last_active_at != null
            ? t("آخر ظهور {date}", { date: dateTime(p.last_active_at) })
            : undefined
        }
        onClick={() => onOpen(p)}
      >
        <strong>{p.name}</strong>
        <small className="person-meta">
          <span className="person-username" dir="ltr">
            @{p.username}
          </span>
          {isFriend && (
            <>
              <span className="meta-separator" aria-hidden="true">
                ·
              </span>
              <span
                className={`person-activity ${p.status}${hasLastActive ? " last-active" : ""}`}
              >
                <ActivityStatus person={p} compact />
              </span>
            </>
          )}
        </small>
      </button>
      {isFriend ? (
        <div className="row-actions">
          <button
            className="icon-button"
            aria-label={t("راسل {name}", { name: p.name })}
            onClick={() => shell.showChat(p)}
          >
            <MessageCircle width={18} height={18} />
          </button>
          <button
            className="icon-button call-action"
            aria-label={t("اتصل بـ{name}", { name: p.name })}
            onClick={() => shell.callFriend(p)}
          >
            <Phone width={18} height={18} />
          </button>
          <button
            className="icon-button call-action"
            aria-label={t("مكالمة فيديو مع {name}", { name: p.name })}
            title={t("مكالمة فيديو")}
            onClick={() => shell.callFriend(p, "video")}
          >
            <Video width={18} height={18} />
          </button>
        </div>
      ) : kind === "incoming" ||
        (p.relation === "pending" && p.requester !== account?.me.id) ? (
        <div className="row-actions">
          <button
            className="button small"
            disabled={submitting}
            onClick={() => void requestAction(p, "accept")}
          >
            <Check width={15} height={15} />
            {t("قبول")}{" "}
          </button>
          <button
            className="icon-button"
            disabled={submitting}
            aria-label={t("رفض طلب {name}", { name: p.name })}
            onClick={() => void requestAction(p, "decline")}
          >
            <X width={16} height={16} />
          </button>
        </div>
      ) : kind === "outgoing" || p.relation === "pending" ? (
        <div className="row-actions">
          <span className="request-sent">
            <Check width={14} height={14} />
            {t("طلب مُرسل")}
          </span>
          <button
            className="icon-button"
            disabled={submitting}
            aria-label={t("إلغاء طلب {name}", { name: p.name })}
            title={t("إلغاء الطلب")}
            onClick={() => void requestAction(p, "cancel-request")}
          >
            <X width={16} height={16} />
          </button>
        </div>
      ) : (
        <button
          className="button secondary small"
          disabled={submitting}
          onClick={() => void requestAction(p, "request")}
        >
          <UserPlus width={15} height={15} />
          {t("إضافة")}{" "}
        </button>
      )}
      {!isFriend && <SafetyMenu person={p} isFriend={false} />}
    </div>
  );
}

export const personFromHistory = (h: CallHistory, friends: Person[]): Person =>
  friends.find((f) => f.id === h.person_id) || {
    id: h.person_id,
    name: h.name,
    username: h.username,
    color: h.color,
    avatar: h.avatar,
    status: "offline",
    guest: h.guest,
  };

export function HistoryRow({ call: h }: { call: CallHistory }) {
  const shell = useShell();
  const account = useAccount();
  const friends = account?.friends ?? [];
  const remove = useRemoveRecent();
  const person = personFromHistory(h, friends);
  const missed = !!account && isMissedCall(h, account.me.id);
  async function removeRecent(callId: string) {
    try {
      await remove.mutateAsync(callId);
      notify(t("تمت إزالة المكالمة من الأخيرة"), "success");
    } catch (error) {
      notify((error as Error).message);
    }
  }
  return (
    <div className="person-row history-row">
      <ProfileAvatar person={person} dot={false} onOpen={shell.openPerson} />
      <button
        className="person-name"
        aria-label={
          h.guest ? undefined : t("عرض حساب {name}", { name: h.name })
        }
        disabled={h.guest}
        onClick={() => shell.openPerson(person)}
      >
        <strong>
          {h.name}
          {h.guest && (
            <span className="guest-label"> · {t("ضيف عبر رابطك")}</span>
          )}
        </strong>
        <small className={missed ? "missed" : ""}>
          {missed ? (
            <PhoneOff width={12} height={12} />
          ) : h.caller === account?.me.id ? (
            <ArrowUpRight width={12} height={12} />
          ) : (
            <ArrowDownLeft width={12} height={12} />
          )}{" "}
          {missed
            ? t("مكالمة فائتة")
            : h.caller === account?.me.id
              ? t("صادرة")
              : t("واردة")}{" "}
          ·{" "}
          {t("{day}، {time}", {
            day: day(h.created_at, true),
            time: time(h.created_at),
          })}
        </small>
      </button>
      {h.connected_at && h.ended_at ? (
        <span className="duration">
          {duration(Math.round((h.ended_at - h.connected_at) / 1000))}
        </span>
      ) : null}
      <button
        className="icon-button"
        disabled={!friends.some((friend) => friend.id === h.person_id)}
        aria-label={t(
          h.media === "video" ? "مكالمة فيديو مع {name}" : "اتصل بـ{name}",
          { name: h.name },
        )}
        onClick={() =>
          shell.callFriend(person, h.media === "video" ? "video" : "audio")
        }
      >
        {h.media === "video" ? (
          <Video width={17} height={17} />
        ) : (
          <Phone width={17} height={17} />
        )}
      </button>
      <SafetyMenu
        person={person}
        isFriend={false}
        onRemoveRecent={() => void removeRecent(h.id)}
      />
    </div>
  );
}
